import FlowApiClient from '../backend/sub_api_clients/FlowApiClient'
import algo from '../core/Algo'
import { FlowAnimationType, IFlowAnimation, IFlowAnimationGroup } from '../definitions/FlowAnimation'
import { NodeType } from '../definitions/Nodes'
import { IExecutionEnvironment, IExecutionMemory } from '../definitions/NodesExecution'
import EventsEngine from '../engines/EventsEngine'
import Helper from '../helpers/Helper'
import FlowUIManager from './FlowUIManager'
import { Queue } from './QueueManager'

class FlowAnimationManagerClass {
    POLLING_INTERVAL = 1000
    isPolling = false
    movieQueue = new Queue<IFlowAnimationGroup>()
    movieList: IFlowAnimationGroup[] = []
    prevExecEnv: IExecutionEnvironment
    _onEndExecution: (execEnv: IExecutionEnvironment) => void
    _onEndAnimation: () => void

    isInAnimation = () => {
        return this.isPolling || !this.movieQueue.isEmpty()
    }

    start = (execEnvId: string, onEndExecution: (execEnv: IExecutionEnvironment) => void, onEndAnimation: () => void) => {
        this.isPolling = true
        this._onEndAnimation = onEndAnimation
        this._onEndExecution = onEndExecution

        this.requestUpdate(execEnvId)
        this.processMovieQueue()
    }

    stop = () => {
        this.isPolling = false
    }

    forceStop = () => {
        this.isPolling = false
        this.movieQueue.clear()
    }

    requestUpdate = async (execEnvId: string) => {
        try {
            if (!this.isPolling) return

            const response = await FlowApiClient.getFlowExecutionEnvironment(execEnvId)
            this.processUpdate(response)

            if (response.status === 'completed' || response.status === 'failed') {
                this.stop()
                this._onEndExecution(response)
                return
            }

            setTimeout(async () => {
                await this.requestUpdate(response.uuid)
            }, this.POLLING_INTERVAL)
        } catch (error) {
            this.isPolling = false
            console.error(error)
        }
    }

    processUpdate = (env: IExecutionEnvironment) => {
        const startAnimation = (memory: IExecutionMemory<NodeType>): IFlowAnimation[] => {
            const animations: IFlowAnimation[] = [
                ...memory.node.inputs
                    .filter(inputNode => memory.inputNodes.includes(inputNode.uuid))
                    .map(inputNode => ({
                        uuid: Helper.generateUUID(),
                        animation: 'executing' as FlowAnimationType,
                        nodeId: memory.node.uuid,
                        nodeName: memory.node.name,
                        edgeId: FlowUIManager._encodeEdge(memory.node, inputNode)
                    })),
                {
                    uuid: Helper.generateUUID(),
                    animation: 'executing',
                    nodeId: memory.node.uuid,
                    nodeName: memory.node.name,
                    delay: memory.depth > 0 ? FlowUIManager.EDGE_ANIMATION_DURATION_MS : 0
                }
            ]
            return animations
        }

        const completeAnimation = (memory: IExecutionMemory<NodeType>, extraDelay?: number): IFlowAnimation[] => {
            return [{
                uuid: Helper.generateUUID(),
                animation: 'completed',
                nodeId: memory.node.uuid,
                nodeName: memory.node.name,
                delay: extraDelay ?? 0
            }]
        }

        const failAnimation = (memory: IExecutionMemory<NodeType>, extraDelay?: number): IFlowAnimation[] => {
            return [{
                uuid: Helper.generateUUID(),
                animation: 'failed',
                nodeId: memory.node.uuid,
                nodeName: memory.node.name,
                delay: extraDelay ?? 0
            }]
        }

        let groups: IFlowAnimationGroup[] = []

        for (let i = 0; i < env.memories.length; i++) {
            const memory = env.memories[i]
            let anims: IFlowAnimation[] = []

            const prevMemory = this.prevExecEnv.memories.find(x => x.uuid === memory.uuid)
            if (!prevMemory) {
                switch (memory.status) {
                    case 'started': {
                        anims = startAnimation(memory)
                        break
                    }
                    case 'completed': {
                        const startAnims = startAnimation(memory)
                        const extraDelay = (startAnims.find(x => x.delay > 0)?.delay ?? 0) + FlowUIManager.EDGE_ANIMATION_DURATION_MS
                        anims = [...startAnims, ...completeAnimation(memory, extraDelay)]
                        break
                    }
                    case 'failed': {
                        const startAnims = startAnimation(memory)
                        const extraDelay = (startAnims.find(x => x.delay > 0)?.delay ?? 0) + FlowUIManager.EDGE_ANIMATION_DURATION_MS
                        anims = [...startAnims, ...failAnimation(memory, extraDelay)]
                        break
                    }
                }
            } else {
                if (prevMemory.status !== memory.status) {
                    switch (memory.status) {
                        case 'started': {
                            anims = startAnimation(memory)
                            break
                        }
                        case 'completed': {
                            anims = completeAnimation(memory)
                            break
                        }
                        case 'failed': {
                            anims = failAnimation(memory)
                            break
                        }
                    }
                }
            }

            // based on the depth do n number of groups
            let group = groups.find(x => x.depth === memory.depth)
            if (!group) {
                group = { depth: memory.depth, animations: [] }
                groups.push(group)
            }

            group.animations = [...group.animations, ...anims]
        }

        if (groups.length > 0) {
            groups = groups.sort((a, b) => a.depth - b.depth)

            for (let i = 0; i < groups.length; i++) {
                const group = groups[i]

                if (group.animations.length > 0) {
                    this.movieQueue.enqueue(group)
                    this.movieList.push(group)
                }
            }
        }

        this.prevExecEnv = env
    }

    processMovieQueue = () => {
        if (this.isPolling || this.movieQueue.size() > 0) {
            // If there is nothing in the queue to animate, then just wait a bit and try again
            if (this.movieQueue.size() === 0) {
                console.log('ANIM - nothing to animate for now')
                setTimeout(() => {
                    this.processMovieQueue()
                }, 200)
                return
            }

            const group = this.movieQueue.dequeue()
            const timeToFinishAllAnimations = algo.max(group.animations.map(x => x.delay ?? 0))

            EventsEngine.publish('flow-animation', group)

            setTimeout(() => {
                this.processMovieQueue()
            }, timeToFinishAllAnimations)
        } else {
            this._onEndAnimation()
        }
    }
}

const FlowAnimationManager = new FlowAnimationManagerClass()
export default FlowAnimationManager
